import { useEffect, Fragment, useRef, useMemo, useCallback, useState } from 'react';
import styled from 'styled-components';
import { Tooltip } from 'react-tooltip';
import { RiArrowDownSLine, RiArrowUpSLine } from 'react-icons/ri';

import HexagonLayer from '../layers/HexagonLayer';
import PhotoLayer from '../layers/PhotoLayer';
import DetectionResults from '../layers/detectionResults/DetectionResults';
import AoiLayer from '../layers/AoiLayer';
import TrainingDataLayer from '../layers/TrainingDataLayer';
import SingleImagePointsLayer from '../layers/singleImage/SingleImagePointsLayer';
import SingleImageFootprinsLayer from '../layers/singleImage/SingleImageFootprintsLayers';
// import TreeCrownGeoJSONLayer from '../layers/TreeCrownGeoJSONLayer';
import ContoursLayer from '../layers/contours/ContoursLayer';
import DroneImagesLayer from '@routes/userRoutes/projects/singleProject/components/layers/droneImages/DroneImagesLayer';

import HexagonColorFilter from '../modifyLayers/hexagonColorFilter/HexagonColorFilter';
import HexagonInfo from './HexagonInfo';
import SidebarTemplate from './sidebarElements/SidebarTemplate';
import SidebarSummary from './summary/SidebarSummary';
// import LayersDropdown from './sidebarElements/LayersDropdown';
import ModelResultDropdown from './sidebarElements/ModelResultDropdown';
import HexagonSummarySidebar from './summary/HexagonSummarySidebar';
import MeasurementSection from './sidebarElements/MeasurementSection';

import { useProject } from '@contexts/Project.context';
import { useAuth } from '@contexts/User.context';
import { MAP_LAYER_NAMES, PROJECT_MODE } from '@utils/constants';

const { ORTHOPHOTO, SINGLE_IMAGE } = PROJECT_MODE;

const ProjectTitle = styled.h2`
	margin: 0;
`;

const Subtitle = styled.div`
	font-weight: 300;
	color: ${p => p.theme.colors.textAccent};
`;

const LayerTitle = styled.div`
	margin: 0;
`;

const LayerListWrapper = styled.div`
	padding: 10px 0 10px 16px;
	> ul {
		row-gap: 5px;
	}
`;

const LayerList = styled.ul`
	margin: 0;
	list-style: none;
	padding: 20px;
	font-size: 1rem;
	font-weight: 600;

	display: flex;
	flex-direction: column;
	row-gap: 25px;
	label {
		text-transform: capitalize;
	}
`;

const LayerSublist = styled.ul`
	margin: 0;
	padding: 0;
	list-style: none;
	display: flex;
	flex-direction: column;
	row-gap: 14px;
	> li {
		> ul {
			list-style: none;
			margin: 0 0 0 25px;
			padding: 0;
		}
	}
`;

const LayerListItem = styled.li``;

const LayerGroupHeader = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	cursor: pointer;
	user-select: none;
	font-size: 0.9rem;
	font-weight: 500;
	color: ${p => p.theme.colors.textAccent};
	text-transform: uppercase;
	letter-spacing: 0.5px;
	margin-bottom: 10px;
	padding: 4px 0;

	&:hover {
		color: ${p => p.theme.colors.textColor};
	}

	svg {
		font-size: 1.2rem;
	}
`;

const LayerGroupContent = styled.div`
	display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
	padding-left: 8px;
	border-left: 3px solid ${p => p.theme.colors.primary};
	margin-bottom: 16px;
`;

const ModelResultHeader = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	padding-bottom: 6px;
`;

const sidebarIds = {
	layerView: 'layerView',
	modifyHexagons: 'modifyHexagons',
	hexagonColorFilter: 'hexagonColorFilter',
	dspColorFilter: 'dspColorFilter',
	contoursSettings: 'contoursSettings',
	droneImagesSettings: 'droneImagesSettings',
};

const LAYER_STARTING_Z_INDEX = 6;

/**
 * Layer Group Component - Collapsible section for grouping layers
 */
const LayerGroup = ({ title, children, defaultOpen = true }) => {
	const [isOpen, setIsOpen] = useState(defaultOpen);

	return (
		<div>
			<LayerGroupHeader onClick={() => setIsOpen(!isOpen)}>
				{isOpen ? <RiArrowUpSLine /> : <RiArrowDownSLine />}
				{title}
			</LayerGroupHeader>
			<LayerGroupContent $isOpen={isOpen}>
				<LayerSublist>{children}</LayerSublist>
			</LayerGroupContent>
		</div>
	);
};

/**
 * Renders the sidebars that are responsible for altering the presentation of the map.
 * anything like changing the styles, showing/hiding layers, filtering etc. should be done here.
 */
const LayersSideBar = () => {
	const { modelType, mapObject, hexagonInfo, project, projectMode, dispatch, setActiveSidebar } =
		useProject();

	const { uuid } = project;

	const modifyLayersSidebarRef = useRef(null);

	const handleLayerViewSidebarSettings = useCallback(
		sidebarId => {
			setActiveSidebar({
				sidebarId,
				parentId: sidebarIds.layerView,
			});
		},
		[setActiveSidebar]
	);

	const handleContoursSettings = useCallback(() => {
		handleLayerViewSidebarSettings(sidebarIds.contoursSettings);
	}, [handleLayerViewSidebarSettings]);

	const handleDroneImagesSettings = useCallback(() => {
		handleLayerViewSidebarSettings(sidebarIds.droneImagesSettings);
	}, [handleLayerViewSidebarSettings]);

	const mapLayers = useMemo(() => {
		// Initialize arrays
		const imageryLayers = [];
		const analysisLayers = [];

		// Add base imagery layers for ORTHOPHOTO mode
		if (projectMode === ORTHOPHOTO) {
			imageryLayers.push({
				key: 'surface-map',
				component: (
					<PhotoLayer
						name="Surface map"
						directory="dsm_tiles"
						zIndex={2}
						visible={false}
						checkDirectory={true}
						layerId="dsmLayer"
					/>
				),
			});
		}

		for (let i = 0; i < project?.map_layers?.length; i++) {
			try {
				const item = project.map_layers[i];
				// TODO: remove this once we finalize things for 'single_image' layer
				if (item.layer === 'single_image') continue;

				if (item.type === 'raster') {
					const [minZoom = 10, maxZoom = 21] = (
						item.zoom_range?.split('-').map(Number) ?? []
					);

					const layerComponent = {
						key: item.layer,
						component: (
							<PhotoLayer
								name={MAP_LAYER_NAMES[item.layer]}
								layerId={item.layer}
								directory=""
								zIndex={LAYER_STARTING_Z_INDEX + i}
								visible={item.layer === 'orthophoto'}
								downloadPath={item.tif_path}
								showOpacityControl={item.layer !== 'orthophoto'}
								tilesPath={item.tiles_path}
								minZoomLevel={minZoom}
								maxZoomLevel={maxZoom}
							/>
						),
					};

					// Categorize layers into ANALYSIS group
					if (
						item.layer === 'canopy_height' ||
						item.layer === 'national_dtm' ||
						item.layer === 'colored_chm'
					) {
						analysisLayers.push(layerComponent);
					} else if (item.layer === 'orthophoto') {
						imageryLayers.push(layerComponent);
					}
					// Note: Other raster layers (if any) would need categorization here
				} else if (item.type === 'vector') {
					if (item.layer === 'contours' && item.files) {
						analysisLayers.push({
							key: 'contours',
							component: (
								<ContoursLayer
									contoursConfig={item}
									visible={false}
									zIndex={LAYER_STARTING_Z_INDEX + i}
									setContoursSettingsActive={handleContoursSettings}
								/>
							),
						});
					} else if (item.layer === 'raw_images') {
						imageryLayers.push({
							key: 'drone-images',
							component: (
								<DroneImagesLayer
									layerConfig={item}
									visible={false}
									zIndex={LAYER_STARTING_Z_INDEX + i}
									setDroneImagesSettingsActive={handleDroneImagesSettings}
								/>
							),
						});
					}
				}
			} catch (e) {
				console.warn(e);
			}
		}

		return { imageryLayers, analysisLayers };
	}, [project, projectMode, uuid, handleContoursSettings, handleDroneImagesSettings]);

	useEffect(() => {
		if (!modifyLayersSidebarRef.current) return;

		dispatch({
			type: 'setModifyLayersSidebarRef',
			payload: modifyLayersSidebarRef,
		});
	}, [modifyLayersSidebarRef?.current]);

	if (!mapObject) return null;

	return (
		<>
			<SidebarTemplate
				sidebarId={sidebarIds.layerView}
				zindex={1}
				header="Layers"
				// headerDropdown={<LayersDropdown />}
				closeable={false}
				collapsible={true}>
				<LayerList>
					<LayerListItem>
						<ProjectTitle>{project?.title}</ProjectTitle>

						<SidebarSummary />
					</LayerListItem>

					{projectMode === ORTHOPHOTO && (
						<>
							{mapLayers.imageryLayers.length > 0 && (
								<LayerGroup title="IMAGERY" defaultOpen={true}>
									{mapLayers.imageryLayers.map(layer => (
										<Fragment key={layer.key}>{layer.component}</Fragment>
									))}
								</LayerGroup>
							)}

							{mapLayers.analysisLayers.length > 0 && (
								<LayerGroup title="ANALYSIS" defaultOpen={true}>
									{mapLayers.analysisLayers.map(layer => (
										<Fragment key={layer.key}>{layer.component}</Fragment>
									))}
								</LayerGroup>
							)}
						</>
					)}

					{projectMode === SINGLE_IMAGE && (
						<LayerListItem>
							<LayerTitle>Map Layers</LayerTitle>

							<LayerListWrapper>
								<LayerSublist>
									<SingleImagePointsLayer />
								</LayerSublist>
							</LayerListWrapper>
						</LayerListItem>
					)}

					<LayerGroup title="MEASUREMENT" defaultOpen={false}>
						<MeasurementSection />
					</LayerGroup>

					{modelType && (
						<LayerListItem>
							<ModelResults />
						</LayerListItem>
					)}
				</LayerList>

				<Tooltip id="tooltip-sidebar-root" variant="light" className="small-tooltip" />
			</SidebarTemplate>

			<div ref={modifyLayersSidebarRef} />

			{hexagonInfo && (
				<>
					<HexagonInfo />

					<HexagonSummarySidebar />

					<HexagonColorFilter sidebarId={sidebarIds.hexagonColorFilter} />
				</>
			)}
		</>
	);
};

export default LayersSideBar;

const ModelResults = () => {
	const { pickedTask, detections } = useProject();

	return (
		<>
			<div>Model</div>

			<div className="mt-1">
				<ModelResultHeader>
					<Subtitle>{pickedTask?.description}</Subtitle>
					{detections?.length > 0 && <ModelResultDropdown />}
				</ModelResultHeader>

				<ModelResultsList />
			</div>
		</>
	);
};

const ModelResultsList = () => {
	const { pickedTask, projectMode, setActiveSidebar } = useProject();
	const { tierPremium, isEnterprise, roleAdmin } = useAuth();
	const { model_uuid: modelUuid } = pickedTask ?? {};

	const showDSP = (tierPremium || isEnterprise || roleAdmin) && projectMode == SINGLE_IMAGE;

	const resultsLayers = [
		{
			key: 'aoi',
			component: <AoiLayer />,
			show: true,
		},
		{
			key: 'detection-results-layer-collection',
			component: <DetectionResults />,
			show: !!modelUuid,
		},
		{
			key: 'training-data',
			component: <TrainingDataLayer />,
			show: projectMode == ORTHOPHOTO,
		},
		{
			key: 'hexagon-layer',
			component: (
				<HexagonLayer
					setModifyHexagonMode={() =>
						setActiveSidebar({
							sidebarId: sidebarIds.modifyHexagons,
							parentId: sidebarIds.layerView,
						})
					}
					setHexagonColorFilterActive={() =>
						setActiveSidebar({
							sidebarId: sidebarIds.hexagonColorFilter,
							parentId: sidebarIds.layerView,
						})
					}
					modifyHexagonsSidebarId={sidebarIds.modifyHexagons}
				/>
			),
			show: true,
		},
		{
			key: 'dspl-layer',
			component: (
				<SingleImageFootprinsLayer
					layerId="dsp-layer"
					layerName="Digital Sample Plot"
					showDsp={true}
					setDspColorFilterActive={() =>
						setActiveSidebar({
							sidebarId: sidebarIds.dspColorFilter,
							parentId: sidebarIds.layerView,
						})
					}
					dspColorFilterSidebarId={sidebarIds.dspColorFilter}
				/>
			),
			show: showDSP,
		},
	];

	return (
		<LayerSublist>
			{resultsLayers.map(
				({ show, key, component }) => show && <Fragment key={key}>{component}</Fragment>
			)}
		</LayerSublist>
	);
};
